<template>
  <v-toolbar class="folder-toolbar"
             app
             fixed
             clipped-left
             color="white"
             v-if="!isEmpty(folder)"
  >
    <span class="folder-type-border" :style="{'border-left-color': folderBorderColor}">&nbsp;</span>
    <v-icon color="amber" class="mr-3" medium>mdi-folder-open</v-icon>
    <v-flex class="pa-2" xs4>
      <v-layout column>
        <v-flex>
          <strong class="grey--text text--darken-2 folder-id">{{ folder.process_number }}</strong>
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <div v-on="on" class="folder-type-name">
                {{ folder.folder_type.name }}
              </div>
            </template>

            {{ folder.folder_type.name }}
          </v-tooltip>
        </v-flex>
      </v-layout>
    </v-flex>

    <folder-secondary-action
      :showActions="showActions"
      @hideActions="showActions = false"
      @showActions="showActions = true"
      @searchDocument="$emit('searchDocument', $event)"
    />
    <v-spacer />
    <transaction-indicator :folder="folder"/>
    <v-spacer />
    <folder-primary-action
      :canBeSentFolder="canBeSentFolder"
      :canBeSentFolderToRevision="canBeSentFolderToRevision"
      :canBeReceiveFolder="canBeReceiveFolder"
      :canBeRequestFolder="canBeRequestFolder"
      :canBeAttached="canBeAttached"
    />
  </v-toolbar>
</template>

<script>
import colors from 'vuetify/es5/util/colors';
import { findIndex,  isEmpty, truncate } from 'lodash';
import TransactionIndicator from '@/components/folder/show/toolbar/TransactionIndicator';
import FolderPrimaryAction from './action/FolderPrimaryAction';
import FolderSecondaryAction from './action/FolderSecondaryAction';
import Highlighter from 'vue-highlight-words'
import {getFolderLastTransaction} from "../../../../mixins/folder-status";

export default {
  name: 'FolderToolbar',
  components: {
    FolderPrimaryAction,
    FolderSecondaryAction,
    TransactionIndicator,
    Highlighter
  },
  data() {
    return {
      showActions: true,
    };
  },
  computed: {
    /**
     * @returns {AdministrativeUnit['id']}
     */
    selectedAdministrativeUnit() {
      return this.$store.getters.selectedAdministrativeUnit;
    },
    lastTransaction() {
      return getFolderLastTransaction(this.folder);
    },
    user() {
      return this.$store.getters.user;
    },
    /**
     * @returns {Folder}
     */
    folder() {
      return this.$store.getters.activeFolder;
    },
    folderViewMode() {
      return this.$store.getters.folderViewMode;
    },

    // Check
    folderHasPending() {
      return this.folderIsEmpty
        || this.folderHasPendingSignature
        || this.folderHasPendingRevision
        || this.folderIsRequestedOwnership;
    },
    folderIsEmpty() {
      return this.folder.documents.length === 0;
    },
    folderIsInSelectedAdministrativeUnit() {
      if (!this.lastTransaction)
        return this.folder.user.id === this.user.id && this.folder.created_in.id === this.selectedAdministrativeUnit;

      return this.lastTransaction.destination.id === this.selectedAdministrativeUnit;
    },
    folderGettingStartedByYou() {
      return Array.isArray(this.folder.transactions) ? this.folder.transactions.length === 0 : false;
    },
    folderHasPendingRevision() {
      return findIndex(this.folder.documents, ['has_pending_revision', true]) !== -1;
    },
    folderHasPendingSignature() {
      return findIndex(this.folder.documents, ['has_pending_signature', true]) !== -1;
    },
    folderHasPendingReceipt() {
      if (!this.lastTransaction) return false;

      return this.lastTransaction.received === false;
    },
    folderIsRequestedOwnership() {
      return this.folder.ownership_requested;
    },
    folderSentToAdministrativeUnit() {
      if (!this.lastTransaction) return false;

      return this.lastTransaction.destination.id === this.selectedAdministrativeUnit;
    },
    /**
     * Pasta foi enviada diretamente para você
     */
    folderSentDirectToYou() {
      if (!this.lastTransaction || !this.lastTransaction.to_user) return false;

      return this.lastTransaction.to_user.id === this.user.id;
    },
    folderTransactionForYou() {
      return this.folderSentDirectToYou || this.folderSentToAdministrativeUnit;
    },
    folderOwnerIsYou() {
      return !!(this.folder.current_owner && this.user && this.folder.current_owner.id === this.user.id);
    },
    folderArchived() {
      return this.$store.getters.folderStatusArchived.name === this.folder.folder_status.name;
    },
    lastFolderTransactionFromYou() {
      if (!this.lastTransaction) return false;

      return this.folder.last_transaction.from_user.id === this.user.id;
    },

    // Can be
    canBeSentFolder() {
      return !this.folderHasPending
        && this.folderOwnerIsYou
        && this.folderIsInSelectedAdministrativeUnit
        && !this.folderHasPendingReceipt
        && !this.folderArchived;
    },
    canBeReceiveFolder() {
      return !this.folderGettingStartedByYou
        && !this.folderOwnerIsYou
        && this.folderIsInSelectedAdministrativeUnit
        && this.folderHasPendingReceipt
        && this.folderTransactionForYou
        && !this.folderArchived
        && !this.lastFolderTransactionFromYou;
    },
    canBeRequestFolder() {
      return !this.folderOwnerIsYou
        && !this.folderTransactionForYou
        && !this.folderArchived;
    },
    canBeSentFolderToRevision() {
      return !this.folderIsEmpty
        && this.folderHasPendingRevision
        && this.folderIsInSelectedAdministrativeUnit
        && this.folderOwnerIsYou;
    },
    canBeAttached() {
      return this.folder.attached_folders.status !== 'attached'
        && !this.folderHasPendingReceipt
        && (this.folderOwnerIsYou
          || this.folderGettingStartedByYou);
    },
    // Border
    folderBorderColor() {
      if (this.folderHasPendingRevision) {
        return colors.amber.base;
      } else if (this.folderHasPendingSignature) {
        return colors.blue.base;
      }
      return colors.grey.base;
    },
  },
  methods: {
    updateViewMode(value) {
      this.$store.dispatch('FOLDER_VIEW_MODE', value);
    },
    truncate,
    isEmpty,
  },
};
</script>

<style scoped>

.folder-type-name{
  overflow: hidden;
  text-overflow: ellipsis;
  width: 35ch;
  white-space: nowrap;
}

.folder-id {
  font-size: 12px;
}
.folder-type-border {
  position: absolute;
  height: 100%;
  left: 1px;
  border-radius: 3px 0 0 3px;
  border-left: 5px solid;
  transition: ease-in-out .15s;
}
.mdi-folder-open {
  -moz-transform: scaleX(-1);
  -o-transform: scaleX(-1);
  -webkit-transform: scaleX(-1);
  transform: scaleX(-1);
}
.role {
  font-size: 12px;
}
</style>



// WEBPACK FOOTER //
// src/components/folder/show/toolbar/FolderToolbar.vue